import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { MessengerService } from './messenger.service';

/**
 * Service class for Contact History.
 */
@Injectable({
  providedIn: 'root'
})
export class ContactHistoryService implements OnDestroy
{
  public patientMoods: Array<{ text: string; value: string; icon: string }> = [
    { text: 'Angry', value: 'angry', icon: 'fa-frown' },
    { text: 'Bad', value: 'bad', icon: 'fa-frown' },
    { text: 'Neutral', value: 'neutral', icon: 'fa-meh' },
    { text: 'Happy', value: 'happy', icon: 'fa-smile' },
    { text: 'Excited', value: 'excited', icon: 'fa-smile' },
    { text: 'N/A', value: 'na', icon: 'fa-question-circle' }
  ];

  private messagingLanguageChangedSubscription: Subscription;

  /**
   * Constructor
   *
   * @param http Auto injected Angular HttpClient
   */
  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private messengerService: MessengerService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get the mood text to display.
   *
   * @param mood Mood value.
   */
  public getMoodDisplayText(mood: string): string
  {
    let text = mood;
    const moodObject = this.patientMoods.find(x => x.value === mood);
    if (moodObject !== undefined)
    {
      text = moodObject.text;
    }
    return text;
  }

  /**
   * Get contact history by id.
   *
   * @param id Contact history ID
   */
  public getContactHistory(id: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/contact/${id}`);
  }

  /**
   * Get contact history sentiment entries for a member.
   *
   * @param id Member ID
   */
  public getMemberContactHistorySentiment(id: number, fromDate?: Date, toDate?: Date): Observable<any[]>
  {
    const paramsArray = [];
    let params = '';
    if (fromDate !== undefined)
    {
      paramsArray.push(`from=${fromDate.toISOString().replace('Z', '')}`);
    }
    if (toDate !== undefined)
    {
      paramsArray.push(`to=${toDate.toISOString().replace('Z', '')}`);
    }
    if (paramsArray.length > 0)
    {
      params = `?${paramsArray.join('&')}`;
    }
    return this.http.get<any[]>(`/contact/member/${id}/history${params}`);
  }

  /**
   * Persist a Contact History in the database.
   *
   * @param data Data to create
   */
  public createContactHistory(data: any): Observable<any>
  {
    return this.http.post('/contact', data);
  }

  /**
   * Soft delete a Contact History in the database.
   *
   * @param id Contact History id to delete
   */
  public deleteContactHistory(id: number): Observable<any>
  {
    return this.http.delete(`/contact/${id}`);
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    this.translate.get('CONTACT-HISTORY-MOODS').subscribe({
      next: (res) =>
      {
        const splitted = res.split(',');
        for (let i = 0; i < splitted.length; i++)
        {
          this.patientMoods[i].text = splitted[i];
        }
      }
    });
  }
}
